import { useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { canUseInternalAdmin } from '../lib/internalUsers';

// Ask SiteScope v2: natural-language questions over the DA database, internal
// users only while we learn what people actually ask.
// The Edge Function runs the model with the caller's own JWT, so answers can
// only contain data this user is entitled to see. v2 adds the structured
// query_das tool server-side, plus bounded follow-up history client-side.
//
// Rendering is plain text (whitespace-pre-wrap) — never raw HTML — so model
// output cannot inject markup. The contract test enforces this.

const ASK_TIMEOUT_MS = 75000;
const MAX_HISTORY_MESSAGES = 6;
const MAX_RESULT_CHIPS = 20;
const SUPABASE_FUNCTIONS_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Every example must be answerable by the current toolset (query_das covers
// category + status + suburb + date-range + cost questions).
const EXAMPLE_QUESTIONS = [
  'dual occs approved in Lennox Head this year',
  'how many new dwellings were lodged in Byron in the last 3 months?',
  'subdivisions over $1M in Tweed this year',
  'what was refused in Ballina in the last 6 months?',
];

export default function AskSiteScope({ theme, userEmail, onOpenDAById }) {
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const placeholder = useMemo(
    () => `Try: "${EXAMPLE_QUESTIONS[Math.floor(Math.random() * EXAMPLE_QUESTIONS.length)]}"`,
    []
  );

  if (!canUseInternalAdmin(userEmail)) return null;

  const invokeAskSiteScope = async (payload) => {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (sessionError || !accessToken) {
      throw new Error('Please sign in again');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ASK_TIMEOUT_MS);
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/ask-sitescope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || `Ask failed (${res.status})`);
      }
      return data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error('Ask timed out — try a simpler question');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const ask = async () => {
    const trimmed = question.trim();
    if (trimmed.length < 3 || asking) return;
    setAsking(true);
    setError(null);
    setResponse(null);
    try {
      const data = await invokeAskSiteScope({ question: trimmed, history: history.slice(-MAX_HISTORY_MESSAGES) });
      if (data?.error) throw new Error(data.error === 'daily_cap_reached' ? 'Daily question limit reached' : data.error);
      setResponse(data);
      if (data?.answer) {
        setHistory(prev => [...prev, { role: 'user', content: trimmed }, { role: 'assistant', content: String(data.answer).slice(0, 700) }].slice(-MAX_HISTORY_MESSAGES));
        setQuestion('');
      }
    } catch (err) {
      setError(err?.message || 'Ask failed');
    } finally {
      setAsking(false);
    }
  };

  const resetConversation = () => {
    setHistory([]);
    setResponse(null);
    setError(null);
    setQuestion('');
  };

  return (
    <div
      data-testid="ask-sitescope"
      style={{
        background: theme.bgCard,
        border: `1px dashed ${theme.accent}66`,
        borderRadius: 14,
        padding: 14,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ color: theme.accent, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          Ask SiteScope
        </span>
        <span style={{ color: theme.textMuted, fontSize: 10 }}>
          internal preview
          {history.length > 0 && (
            <button
              onClick={resetConversation}
              style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textMuted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              New chat
            </button>
          )}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') ask(); }}
          placeholder={history.length > 0 ? 'Ask a follow-up…' : placeholder}
          maxLength={400}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 8,
            border: `1px solid ${theme.border}`, background: theme.bg || 'transparent',
            color: theme.text, fontSize: 'var(--text-input, 16px)', fontFamily: 'inherit', outline: 'none',
          }}
        />
        <button
          onClick={ask}
          disabled={asking || question.trim().length < 3}
          style={{
            padding: '10px 16px', borderRadius: 8, border: 'none',
            background: theme.accent, color: '#0a0f1a', fontWeight: 700,
            fontSize: 'var(--text-sm)', fontFamily: 'inherit',
            cursor: asking ? 'default' : 'pointer', opacity: asking || question.trim().length < 3 ? 0.6 : 1,
          }}
        >
          {asking ? 'Asking…' : 'Ask'}
        </button>
      </div>

      {error && (
        <div style={{ color: theme.accent, fontSize: 12, marginTop: 8 }}>{error}</div>
      )}

      {response?.answer && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: theme.text, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {response.answer}
          </div>
          {Array.isArray(response.da_ids) && response.da_ids.length > 0 && typeof onOpenDAById === 'function' && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {response.da_ids.slice(0, MAX_RESULT_CHIPS).map((daId, index) => (
                <button
                  key={daId}
                  onClick={() => onOpenDAById(daId)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    border: `1px solid ${theme.border}`, background: 'transparent',
                    color: theme.accent, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Open result {index + 1}
                </button>
              ))}
            </div>
          )}
          <div style={{ color: theme.textMuted, fontSize: 10, marginTop: 6 }}>
            {response.usage ? `${response.usage.tool_calls} lookups · ~$${Number(response.usage.est_cost_usd || 0).toFixed(4)}` : ''}
            {' · answers come only from SiteScope data'}
          </div>
        </div>
      )}
    </div>
  );
}
